import React, {Suspense} from 'react'
import classnames from '@helpers/misc/classnames'
import FormatNumber from '@i18n/formatNumber/Component'
import usePreferences from '@page-components/CheckoutV3/hooks/usePreferences.main'
import get from 'lodash/get'
import {useTranslation} from 'next-i18next'

import WebsiteCoinsImage from '../../WebsiteCoins/WebsiteCoinsCount/WebsiteCoinsImage'
import showTip from '../helpers/showTip'
import SummarySkeleton from '../SummarySkeleton'

import Bags from './Bags'
import Points from './Points'
import ServiceFee from './ServiceFee'
import useMoneySummary from './useMoneySummary.main'

const renderLine = (label, value, className?) => (
  <div className={classnames('flex text-sm', className)}>
    <div className="flex-1 font-semibold">{label}</div>
    <div>{value}</div>
  </div>
)

const renderDiscount = (discount, className?) => (
  <div
    className={classnames(
      'px-1 bg-green-50 text-green-700 rounded font-medium ml-2 text-sm',
      className
    )}>
    {discount}
  </div>
)

function Money() {
  const {t: tWebsites} = useTranslation('checkoutv3', {keyPrefix: 'cart'})
  const {preferences: generalPrefs} = usePreferences()
  const preferences = useMoneySummary()
  const {cart, deliveryType} = preferences
  const {calculatedTipAmount} = cart
  const hasLoyaltyProgramPoints =
    !!generalPrefs.loyaltyProgram?.transactionId && !!cart?.loyaltyTransactionStatus?.transactionId
  const hasWebsiteCoins = !!preferences.websiteCoinsToSpend
  const totalDiscounts =
    cart.realPrice - cart.subtotalWithProductDiscount + (cart?.couponStatus?.discount || 0)

  return (
    <div className="flex flex-col space-y-4 mt-4">
      {renderLine(tWebsites('productsTotalRealPrice'), <FormatNumber value={cart.realPrice} />)}
      {totalDiscounts > 0
        ? renderLine(
            tWebsites('totalDiscounts'),
            renderDiscount(
              <FormatNumber value={totalDiscounts} render={number => <>{`- ${number}`}</>} />
            )
          )
        : null}
      {hasLoyaltyProgramPoints
        ? renderLine(
            tWebsites('payLoyaltyPoints', {
              loyaltyPointsName: get(cart, 'loyaltyTransactionStatus.loyaltyName', 'puntos')
            }),
            renderDiscount(
              <FormatNumber
                value={get(cart, 'loyaltyTransactionStatus.discount', 0)}
                render={number => <>{`- ${number}`}</>}
              />
            )
          )
        : null}
      {hasWebsiteCoins
        ? renderLine(
            <div className="flex items-center">
              <WebsiteCoinsImage svgClass={'primaryColor'} />
              <div className="ml-1">
                {tWebsites('payWebsitePoints', {
                  websiteCoinsName: get(cart, 'pointsDetails.name', 'puntos')
                })}
              </div>
            </div>,
            renderDiscount(
              <FormatNumber
                value={get(cart, 'websiteCoinsToSpend', 0)}
                render={number => <>{`- ${number}`}</>}
              />
            )
          )
        : null}
      <ServiceFee />
      {showTip(preferences)
        ? renderLine(
            deliveryType === 'delivery' ? 'Propina para el repartidor' : 'Propina',
            <FormatNumber value={calculatedTipAmount} />
          )
        : null}
      {generalPrefs.website.bagCost ? <Bags /> : null}
      {renderLine(
        'Total a pagar',
        <FormatNumber value={cart.amountToPay} />,
        'mt-4 font-bold text-base'
      )}
      <Points />
    </div>
  )
}

export default function WrappedMoney() {
  return (
    <Suspense fallback={<SummarySkeleton />}>
      <Money />
    </Suspense>
  )
}
